import * as React from 'react';
import { ApiHooksCaching } from './caching';
import { ApiHooksStore } from './store';
import { ApiHooksSystemSettings } from './systemSettings';
import { ApiHooksGlobal } from './global';
import { Objects } from '../utils/objects';
import { ApiHooksEvents } from './events';
import { ApiHooksResponders } from './responders';
/**
 * API Hooks
 * ----------------
 * Parsing of the generated API client into a set of hooks.
 *
 * This file contains everything relating to the hooks themselves, includes:
 * - The hook creation entry point
 * - The useQuery and useMutation hooks
 * - The creation of the empty mock endpoint + default settings dictionaries
 */
export var ApiHooks;
(function (ApiHooks) {
    /** UTILITY TYPES */
    /** UTILITY FUNCTIONS */
    /**
     * Root logging function for debug mode, just a proxy for the console log for now.
     * @param messages The items to log
     */
    function log(...messages) {
        // eslint-disable-next-line no-console
        console.log(...messages);
    }
    /**
     * Root warning function for debug mode, just a proxy for the console warn for now.
     * @param messages The items to warn
     */
    function warn(...messages) {
        // eslint-disable-next-line no-console
        console.warn(...messages);
    }
    /** CREATION FUNCTIONS */
    /**
     * Creates and empty dictionary of config for each endpoint to be populated outside this file at application level
     * @param apiClient The API client to parse and create an entry for each controller, and then another for each endpoint.
     * @returns The empty dictionary ready for populating in `../endpointSettings.ts` and `../mockEndpoints.ts`
     */
    function createEmptyHookLibraryDefaults(apiClient) {
        return Object.keys(apiClient).reduce((controllerMemo, controllerKey) => {
            const newControllerMemo = Object.assign({}, controllerMemo);
            newControllerMemo[controllerKey] = Object.keys(apiClient[controllerKey]).reduce((endpointMemo, endpointKey) => {
                const newEndpointMemo = Object.assign({}, endpointMemo);
                newEndpointMemo[endpointKey] = {};
                return newEndpointMemo;
            }, {});
            return newControllerMemo;
        }, {});
    }
    /**
     * Creates the useQuery and useMutation hooks for each endpoint in a controller
     * @param rootKey A string unique to the controller
     * @param controller The controller object
     * @param rootQuerySettings The query settings - system level with application level applied on top
     * @param rootMutationSettings The mutation settings - system level with application level applied on top
     * @param rootRequestSettings The settings for the useRequest hook - system level with application level applied on top
     * @param hookConfig The dictionary of config - populated in `../endpointSettings.ts`
     * @param mockEndpointLibrary The dictionary of mock endpoints - populated in `../mockEndpoints.ts`
     * @param defaultDataLibrary The dictionary of default data - populated in `../defaultData.ts`
     * @returns The useQuery and useMutation hooks
     */
    function createHooks(rootKey, controller, rootQuerySettings, rootMutationSettings, rootRequestSettings, hookConfig, mockEndpointLibrary, defaultDataLibrary, generalConfig, processingHook, preProcessorHook) {
        // Reduce the controller endpoints to produce an object for each one with the two hooks on it.
        return Object.keys(controller).reduce((incomingControllerDictionary, endpointKey) => {
            var _a, _b, _c, _d, _e, _f;
            // A string unique to the endpoint - combines the controller and endpoint names
            const endpointHash = `${rootKey}.${endpointKey}`;
            // fetch endpoint level query settings if available and apply on top of the passed in system and application level settings
            const querySettings = (_b = (_a = hookConfig[endpointKey]) === null || _a === void 0 ? void 0 : _a.query) !== null && _b !== void 0 ? _b : {};
            const combinedQuerySettings = Objects.mergeDeep(rootQuerySettings, querySettings);
            // fetch endpoint level mutation settings if available and apply on top of the passed in system and application level settings
            const mutationSettings = (_d = (_c = hookConfig[endpointKey]) === null || _c === void 0 ? void 0 : _c.mutation) !== null && _d !== void 0 ? _d : {};
            const combinedMutationSettings = Objects.mergeDeep(rootMutationSettings, mutationSettings);
            // fetch endpoint level request parameters if available and apply on top of the passed in system and application level settings
            const requestSettings = (_f = (_e = hookConfig[endpointKey]) === null || _e === void 0 ? void 0 : _e.request) !== null && _f !== void 0 ? _f : {};
            const combinedRequestSettings = Objects.mergeDeep(rootRequestSettings, requestSettings);
            // create two promise factories - one returns the actual endpoint promise, the other returns the mock endpoint if it's been created
            const promiseFactory = (arg) => controller[endpointKey](arg);
            const mockPromiseFactory = mockEndpointLibrary[endpointKey] && ((arg, testKey) => mockEndpointLibrary[endpointKey](arg, testKey));
            // store an initial application started date/time
            const applicationStartedTimestamp = Date.now();
            // LOGGER FUNCTIONS
            /**
             * Logs messages from useQuery hooks to the console if in debug mode
             * - includes some data about the controller and endpoint
             * - calls the root logging utility function
             */
            const queryLog = (messages, debugKey) => {
                if (generalConfig === null || generalConfig === void 0 ? void 0 : generalConfig.debugMode) {
                    log(`API Hooks Query, Endpoint: ${endpointHash}${debugKey ? ` - ${debugKey}` : ''} -`, ...messages);
                }
            };
            /**
             * Logs messages from useMutation hooks to the console if in debug mode
             * - includes some data about the controller and endpoint
             * - calls the root logging utility function
             */
            const mutationLog = (messages, debugKey) => {
                if (generalConfig === null || generalConfig === void 0 ? void 0 : generalConfig.debugMode) {
                    log(`API Hooks Mutation, Endpoint: ${endpointHash}${debugKey ? ` - ${debugKey}` : ''} -`, ...messages);
                }
            };
            /**
             * Logs messages from useRequest hooks to the console if in debug mode
             * - includes some data about the controller and endpoint
             * - calls the root logging utility function
             */
            const requestLog = (messages, debugKey) => {
                if (generalConfig === null || generalConfig === void 0 ? void 0 : generalConfig.debugMode) {
                    log(`API Hooks Request, Endpoint: ${endpointHash}${debugKey ? ` - ${debugKey}` : ''} -`, ...messages);
                }
            };
            /**
             * Logs messages from useTools hooks to the console if in debug mode
             * - includes some data about the controller and endpoint
             * - calls the root logging utility function
             */
            const toolsLog = (messages, debugKey) => {
                if (generalConfig === null || generalConfig === void 0 ? void 0 : generalConfig.debugMode) {
                    log(`API Hooks Tools, Endpoint: ${endpointHash}${debugKey ? ` - ${debugKey}` : ''} -`, ...messages);
                }
            };
            /**
             * An in memory storage container to avoid showing multiple config warnings for the same endpoint
             */
            const endpointWarningsShown = [];
            /**
             * Called when an endpoint is used with either a query or mutation hook.
             * Used to log a warning if the relevant config is missing.
             * @param type The type of hook that's been used
             */
            const endpointUsed = (type) => {
                if ((generalConfig === null || generalConfig === void 0 ? void 0 : generalConfig.showMissingConfigWarnings) &&
                    !endpointWarningsShown.some((e) => e === endpointHash) &&
                    ((!Object.keys(querySettings).length && type === 'query') || (!Object.keys(mutationSettings).length && type === 'mutation'))) {
                    const configExample = type === 'query' ? 'cache keys' : 'refetch queries';
                    warn(`API Hooks WARNING! - The endpoint "${endpointHash}" has been used as a ${type} without any ${type} config defined at endpoint level. Do you need to add any config? (i.e. ${configExample})`);
                    endpointWarningsShown.push(endpointHash);
                }
            };
            /**
             * Reducer used by useMutation to store live response state
             * @param state The current live response state
             * @param action A partial state to override the current
             * @returns The updated state
             */
            const mutationFetchResponseReducer = (state, action) => {
                return Object.assign(Object.assign({}, state), action);
            };
            const controllerDictionary = Object.assign({}, incomingControllerDictionary);
            controllerDictionary[endpointKey] = {
                /**
                 * useQuery
                 * - The hook to be used if the endpoint is a GET and should interface with the caching system
                 * - Receives the hook execution level settings to override the system, application and hook level.
                 * - Returns the live data and an optional fetch method to manually invoke
                 * - Can only be used within a React Function Component
                 */
                useQuery: (executionSettings = {}) => {
                    /** MARK ENDPOINT AS USED */
                    React.useEffect(() => {
                        endpointUsed('query');
                    }, []);
                    /** GATHER DATA AND SETTINGS */
                    // listen to the context from the store - this state is updated via the central reducer - see `./store.tsx`
                    const [state, dispatch, testKeys] = React.useContext(ApiHooksStore.Context);
                    // settings - apply the hook execution settings (if any) to the passed in system, application and endpoint level.
                    // NOTE - the JSON.stringify prevents the need for the consumer to memoize the incoming execution settings, it's not ideal, but it's only a small object so it should be ok.
                    const settingsFromHook = React.useMemo(() => {
                        return Objects.mergeDeep(combinedQuerySettings, executionSettings);
                    }, [
                        JSON.stringify(executionSettings),
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchComplete,
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchError,
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchStart,
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchSuccess,
                    ]);
                    // retrieve the pre-processor if available
                    const preProcessor = preProcessorHook === null || preProcessorHook === void 0 ? void 0 : preProcessorHook();
                    // cache key - retrieve any cache key value if one exists in the settings
                    const cacheKeyValueFromHook = React.useMemo(() => {
                        const { cacheKey, parameters } = settingsFromHook;
                        return ApiHooksCaching.parseCacheKey(parameters, cacheKey);
                    }, [settingsFromHook]);
                    // parameters - create a unique string from the parameters used in the request - this allows us to detect whether the params of a request have changed and re-invoke
                    const paramHashFromHook = React.useMemo(() => {
                        return ApiHooksCaching.hashParams(settingsFromHook.parameters);
                    }, [settingsFromHook]);
                    // storing the cache key in state means we can update it if the parameters change - either through the hook settings or a manual invoke
                    const [cacheKey, setCacheKey] = React.useState(cacheKeyValueFromHook);
                    // store the last used fetch settings in a ref so that they can be passed to the processing hook.
                    const lastUsedSettings = React.useRef();
                    // first render ref - for update effect
                    const isFirstRender = React.useRef(true);
                    // state - Fetch the state slice from the global state object (if exists) based on the cache key value if passed, or the default cache key
                    const storedStateSlice = React.useMemo(() => {
                        var _a;
                        const currentStoredStateSlice = (_a = state[endpointHash]) === null || _a === void 0 ? void 0 : _a[cacheKey];
                        // check for default data settings and use that initially if appropriate.
                        if (!currentStoredStateSlice && settingsFromHook.useDefaultData) {
                            const defaultDataFactory = defaultDataLibrary[endpointKey];
                            if (!defaultDataFactory) {
                                throw new Error(`API Hooks error - no default data has been defined for the following query: ${endpointHash}, did you mean to set "useDefaultData" to "true"?`);
                            }
                            const defaultDataValue = defaultDataFactory(settingsFromHook.parameters);
                            queryLog([`Using default data`, { defaultData: defaultDataValue }], settingsFromHook.debugKey);
                            return {
                                paramHash: paramHashFromHook,
                                data: defaultDataValue,
                                status: 'loaded',
                                timestamp: applicationStartedTimestamp,
                                shouldRefetchData: undefined,
                                maxCachingDepth: settingsFromHook.maxCachingDepth,
                            };
                        }
                        // check for "initialData" passed to hook, and use that on first render
                        if (!currentStoredStateSlice && settingsFromHook.initialData) {
                            queryLog([`Using initial data`, { initialData: settingsFromHook.initialData }], settingsFromHook.debugKey);
                            return {
                                paramHash: paramHashFromHook,
                                data: settingsFromHook.initialData,
                                status: 'loaded',
                                timestamp: applicationStartedTimestamp,
                                shouldRefetchData: undefined,
                                maxCachingDepth: settingsFromHook.maxCachingDepth,
                            };
                        }
                        return currentStoredStateSlice;
                    }, [state[endpointHash], cacheKey, settingsFromHook]);
                    /**
                     * Effect to handle the cache key changing without a re-mount.
                     * - Essentially forces a virtual re-mount with the new state slice
                     */
                    React.useLayoutEffect(() => {
                        isFirstRender.current = true;
                        setCacheKey(cacheKeyValueFromHook);
                    }, [cacheKeyValueFromHook]);
                    /**
                     * Effect to set default data to state
                     */
                    React.useLayoutEffect(() => {
                        var _a;
                        if (storedStateSlice && !((_a = state[endpointHash]) === null || _a === void 0 ? void 0 : _a[cacheKey])) {
                            // To get here, we must have returned some default data that isn't stored in cache. We need to store it now.
                            queryLog([`Storing initial data in cache`, { state: storedStateSlice }], settingsFromHook.debugKey);
                            dispatch === null || dispatch === void 0 ? void 0 : dispatch(ApiHooksStore.Actions.loaded(endpointHash, storedStateSlice.paramHash, cacheKey, storedStateSlice.data, settingsFromHook === null || settingsFromHook === void 0 ? void 0 : settingsFromHook.maxCachingDepth, undefined, true));
                        }
                    }, [state[endpointHash], cacheKey, storedStateSlice]);
                    /** CACHE READER */
                    /**
                     * Reads the caching settings and data to ascertain whether the data is valid in it's current state.
                     */
                    const isCacheValid = React.useMemo(() => {
                        let valid = false;
                        let validOnError = false;
                        if (storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.data) {
                            // it's safe to cast this out of partial here, as we know the system defaults have been loaded
                            const cachingSettings = settingsFromHook.caching;
                            valid = !ApiHooksCaching.isStale(storedStateSlice, cachingSettings.staleIfOlderThan);
                            validOnError = !ApiHooksCaching.isStale(storedStateSlice, cachingSettings.staleOnErrorIfOlderThan);
                        }
                        queryLog([
                            `Cache validity loaded/updated`,
                            {
                                settings: settingsFromHook,
                                valid,
                                validOnError,
                            },
                        ], settingsFromHook.debugKey);
                        if ((storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.error) || (storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.status) === 'aborted') {
                            return validOnError;
                        }
                        return valid;
                    }, [storedStateSlice, settingsFromHook]);
                    // value - create the data value to return from the state slice
                    const valueToReturn = React.useMemo(() => {
                        queryLog([
                            `Current state loaded/updated`,
                            {
                                settings: settingsFromHook,
                                cacheData: storedStateSlice !== null && storedStateSlice !== void 0 ? storedStateSlice : 'none',
                                cacheKey,
                            },
                        ], settingsFromHook.debugKey);
                        return {
                            error: storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.error,
                            isFetching: (storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.status) === 'loading-auto' ||
                                (storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.status) === 'loading-manual' ||
                                (storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.status) === 'loading-refetch',
                            fetchingMode: ApiHooksStore.fetchingModeFromStateSliceStatus(storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.status),
                            data: storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.data,
                            endpointID: endpointHash,
                        };
                    }, [storedStateSlice, settingsFromHook]);
                    /** FETCHERS AND INVOKERS */
                    // fetcher - the fetch method - attempts to retrieve data from server, regardless of caching
                    const fetch = React.useCallback(async (fetchSettings, finalParamHash, mode = 'auto') => {
                        var _a, _b, _c, _d, _e, _f, _g;
                        // get the new cache key if applicable, and set it to state, thus potentially updating the state slice returned from the hook.
                        const finalCacheKey = ApiHooksCaching.parseCacheKey(fetchSettings.parameters, fetchSettings.cacheKey);
                        setCacheKey(finalCacheKey);
                        // check the global live fetching log to avoid simultaneous requests being fired before react has processed the state changes.
                        if (ApiHooksGlobal.isFetching(endpointHash, finalCacheKey) && !fetchSettings.allowSimultaneousRequests) {
                            queryLog([
                                'Fetching aborted, request already in progress',
                                {
                                    settings: fetchSettings,
                                    paramHash: finalParamHash,
                                    cacheKey: finalCacheKey,
                                },
                            ], fetchSettings.debugKey);
                            return;
                        }
                        // set the endpoint to "fetching" in the live fetching log, to prevent duplicate requests from being fired on component load.
                        ApiHooksGlobal.setFetching(endpointHash, finalCacheKey, true);
                        queryLog(['Fetching', { settings: fetchSettings, paramHash: finalParamHash, cacheKey: finalCacheKey }], fetchSettings.debugKey);
                        // dispatch the loading action to change the fetching state
                        dispatch === null || dispatch === void 0 ? void 0 : dispatch(ApiHooksStore.Actions.loading(endpointHash, finalParamHash, finalCacheKey, mode, fetchSettings.maxCachingDepth));
                        // call the pre-processor if it exists
                        const shouldContinue = preProcessor
                            ? await preProcessor({ hookType: 'query', endpointID: endpointHash, settings: fetchSettings })
                            : true;
                        // abort if pre-processor returns false
                        if (!shouldContinue) {
                            ApiHooksGlobal.setFetching(endpointHash, finalCacheKey, false);
                            dispatch === null || dispatch === void 0 ? void 0 : dispatch(ApiHooksStore.Actions.aborted(endpointHash, finalParamHash, finalCacheKey, fetchSettings.maxCachingDepth));
                            queryLog([`Query aborted by pre-processor`, { fetchSettings }], fetchSettings.debugKey);
                            return;
                        }
                        ApiHooksEvents.onFetchStart.executeEventHooks(endpointHash, fetchSettings.parameters, 'query');
                        (_a = fetchSettings.onFetchStart) === null || _a === void 0 ? void 0 : _a.call(fetchSettings, fetchSettings, mode);
                        // store a copy of the previous fetch params before they get overwritten with the new ones
                        const previousParams = Object.assign({}, ((_c = (_b = lastUsedSettings.current) === null || _b === void 0 ? void 0 : _b.parameters) !== null && _c !== void 0 ? _c : {}));
                        // store the final settings for the processing hook
                        lastUsedSettings.current = Object.assign({}, fetchSettings);
                        // set up a try/catch - we're about to make the actual request
                        let value;
                        let error;
                        try {
                            // fetch the data value from either the real or mock endpoint, depending on the settings
                            if (testKeys || fetchSettings.useMockEndpoints) {
                                if (!mockPromiseFactory) {
                                    throw new Error(`API Hooks error - no mock endpoint has been defined for the following query: ${endpointHash}`);
                                }
                                value = await mockPromiseFactory(fetchSettings.parameters, (_d = testKeys === null || testKeys === void 0 ? void 0 : testKeys[endpointHash]) === null || _d === void 0 ? void 0 : _d.testKey);
                            }
                            else {
                                value = await promiseFactory(fetchSettings.parameters);
                            }
                            // send the data to the store by despatching the loaded action
                            queryLog(['Fetch successful, with result:', value], fetchSettings.debugKey);
                            dispatch === null || dispatch === void 0 ? void 0 : dispatch(ApiHooksStore.Actions.loaded(endpointHash, finalParamHash, finalCacheKey, fetchSettings.payloadModifier
                                ? fetchSettings.payloadModifier(storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.data, value, previousParams, fetchSettings.parameters)
                                : value, fetchSettings.maxCachingDepth));
                            ApiHooksEvents.onFetchSuccess.executeEventHooks(endpointHash, fetchSettings.parameters, 'query', value);
                            (_e = fetchSettings.onFetchSuccess) === null || _e === void 0 ? void 0 : _e.call(fetchSettings, value, fetchSettings);
                        }
                        catch (e) {
                            // an error has been thrown by the server, catch it and set it in state, otherwise throw it to the consumer.
                            error = e;
                            queryLog(['Fetch failed, with error:', error], fetchSettings.debugKey);
                            dispatch === null || dispatch === void 0 ? void 0 : dispatch(ApiHooksStore.Actions.error(endpointHash, finalParamHash, finalCacheKey, error, fetchSettings.maxCachingDepth));
                            ApiHooksEvents.onFetchError.executeEventHooks(endpointHash, fetchSettings.parameters, 'query', error);
                            (_f = fetchSettings.onFetchError) === null || _f === void 0 ? void 0 : _f.call(fetchSettings, error, fetchSettings);
                        }
                        finally {
                            // set the request as finished fetching in the live fetching log so that future requests won't be aborted.
                            ApiHooksGlobal.setFetching(endpointHash, finalCacheKey, false);
                            (_g = fetchSettings.onFetchComplete) === null || _g === void 0 ? void 0 : _g.call(fetchSettings, value, error, fetchSettings);
                            // run responder listeners
                            ApiHooksResponders.registeredQueryListeners
                                .filter((rl) => rl.endpointHash === endpointHash)
                                .forEach((rl) => {
                                queryLog(['Executing query responder listener'], fetchSettings.debugKey);
                                rl.callback({ data: value, error, cacheKey: finalCacheKey, params: fetchSettings.parameters, settings: fetchSettings });
                            });
                        }
                    }, [dispatch, setCacheKey, storedStateSlice, testKeys, preProcessor]);
                    // invoke - called when the component mounts if autoInvoke = true, and from the manual invoke method
                    // checks whether data should be fetched based on cache settings, error status & whether the params have changed
                    const invoke = React.useCallback((param, fetchSettings, mode = 'auto', forceExclusiveParams = false) => {
                        var _a;
                        // merge any params into existing settings passed from higher levels
                        const finalSettings = Objects.mergeDeep(settingsFromHook, { parameters: param || {} });
                        // if the manual invoke has been used with a payload modifier, add it on here.
                        if (fetchSettings === null || fetchSettings === void 0 ? void 0 : fetchSettings.payloadModifier) {
                            finalSettings.payloadModifier = fetchSettings.payloadModifier;
                        }
                        // read force network setting, from query setting first, then fetch settings if present.
                        const forceNetwork = fetchSettings === null || fetchSettings === void 0 ? void 0 : fetchSettings.forceNetwork;
                        // is cache stale or absent?
                        const cacheIsStaleOrAbsent = !isCacheValid;
                        // check for bookmark parameters, and read the stored value if appropriate
                        if (((_a = finalSettings.caching) === null || _a === void 0 ? void 0 : _a.bookmarkParameters) && (valueToReturn === null || valueToReturn === void 0 ? void 0 : valueToReturn.data) && (storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.paramHash) && !cacheIsStaleOrAbsent) {
                            // get current values for incoming bookmark parameters, stripping out falsy values
                            const bookmarkPartial = ApiHooksCaching.parseBookmarksIntoParamPartial(finalSettings.parameters, finalSettings.caching.bookmarkParameters);
                            // get previous values for incoming bookmarks
                            const parsedHash = JSON.parse(storedStateSlice.paramHash);
                            const previousValues = ApiHooksCaching.parseBookmarksIntoParamPartial(parsedHash, finalSettings.caching.bookmarkParameters);
                            // Spread previous values first, and then incoming values where not falsy, to make sure incoming value wins where defined, but previous value wins where undefined.
                            finalSettings.parameters = Objects.mergeDeep(finalSettings.parameters || {}, previousValues, bookmarkPartial);
                            queryLog([
                                'Loaded stored bookmark params where incoming param is undefined',
                                { incomingBookmarks: bookmarkPartial, storedBookmarks: previousValues },
                            ], finalSettings.debugKey);
                        }
                        if (forceExclusiveParams && param) {
                            finalSettings.parameters = param;
                        }
                        // create a new param hash for comparison, we should make sure we invoke if the params are different to what's cached.
                        const newParamHash = ApiHooksCaching.hashParams(finalSettings.parameters);
                        // create a set of booleans containing information about the current state of the request/caching.
                        const inErrorState = !!(storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.error);
                        const alreadyFetching = !!(valueToReturn === null || valueToReturn === void 0 ? void 0 : valueToReturn.isFetching);
                        const paramsAreDifferent = !!finalSettings.invokeOnParamChange && !!storedStateSlice && storedStateSlice.paramHash !== newParamHash;
                        const refetchTriggerSet = !!(storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.shouldRefetchData);
                        // The logic which dictates whether to invoke a request, or whether we can use what we already have in cache.
                        const shouldLoad = (cacheIsStaleOrAbsent && !alreadyFetching) || inErrorState || paramsAreDifferent || forceNetwork || refetchTriggerSet;
                        queryLog([
                            shouldLoad ? 'Invoking fetcher' : 'Cache loaded',
                            {
                                inErrorState,
                                cacheIsStaleOrAbsent,
                                alreadyFetching,
                                paramsAreDifferent,
                                forceNetwork,
                                refetchTriggerSet,
                            },
                        ], finalSettings.debugKey);
                        if (shouldLoad) {
                            fetch(finalSettings, newParamHash, mode);
                        }
                    }, [storedStateSlice, valueToReturn, settingsFromHook, fetch, isCacheValid]);
                    // the manual invoke method is really just a proxy for invoke, with a bit of logging and settings application. returned as index 1 of the hook response for manual fetching
                    const manualInvoke = React.useCallback((...args) => {
                        var _a;
                        queryLog(['Manual invoke triggered'], settingsFromHook.debugKey);
                        // apply default forceNetwork setting to manual invoke
                        const newArgs = [...args];
                        if (((_a = newArgs[1]) === null || _a === void 0 ? void 0 : _a.forceNetwork) === undefined) {
                            newArgs[1] = Object.assign(Object.assign({}, (newArgs[1] || {})), { forceNetwork: settingsFromHook.forceNetworkOnManualInvoke });
                        }
                        invoke(newArgs[0], newArgs[1], 'manual');
                    }, [invoke, settingsFromHook]);
                    // the manual state setter
                    const manualSet = React.useCallback((newState, overrideSettings) => {
                        const finalSettings = Object.assign(Object.assign({}, settingsFromHook), (overrideSettings !== null && overrideSettings !== void 0 ? overrideSettings : {}));
                        const finalCacheKey = ApiHooksCaching.parseCacheKey(finalSettings.parameters, finalSettings.cacheKey);
                        const finalParamHash = ApiHooksCaching.hashParams(finalSettings.parameters);
                        dispatch === null || dispatch === void 0 ? void 0 : dispatch(ApiHooksStore.Actions.loaded(endpointHash, finalParamHash, finalCacheKey, newState, finalSettings.maxCachingDepth));
                    }, [settingsFromHook]);
                    /** FLOW MANAGEMENT EFFECTS */
                    // Manage endpoint mount status
                    React.useEffect(() => {
                        ApiHooksGlobal.setMounted(endpointHash, cacheKey);
                        return () => ApiHooksGlobal.setUnMounted(endpointHash, cacheKey);
                    }, [cacheKey]);
                    /** INVOCATION TRIGGER EFFECTS */
                    // called when the component mounts, checks whether to invoke based on settings
                    React.useLayoutEffect(() => {
                        if (settingsFromHook.autoInvoke) {
                            queryLog(['Auto invoke triggered'], settingsFromHook.debugKey);
                            if (settingsFromHook.holdInvokeForCacheKeyParam &&
                                settingsFromHook.cacheKey &&
                                ApiHooksCaching.cacheKeyIsDefault(settingsFromHook.parameters, settingsFromHook.cacheKey)) {
                                queryLog(['Invoke held - cache key property has falsy value', { settings: settingsFromHook }], settingsFromHook.debugKey);
                            }
                            else {
                                invoke();
                            }
                        }
                    }, [!!settingsFromHook.autoInvoke, cacheKey]);
                    // called when the params passed into the hook CHANGE, but NOT on first run - sets the new params/cache key and fetches data if the settings allow.
                    React.useEffect(() => {
                        if (!isFirstRender.current) {
                            queryLog(['Parameters changed', { oldParams: storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.paramHash, newParams: paramHashFromHook }], settingsFromHook.debugKey);
                            if (settingsFromHook.invokeOnParamChange) {
                                if (settingsFromHook.holdInvokeForCacheKeyParam &&
                                    settingsFromHook.cacheKey &&
                                    ApiHooksCaching.cacheKeyIsDefault(settingsFromHook.parameters, settingsFromHook.cacheKey)) {
                                    queryLog(['Fetch held - cache key property has falsy value', { settings: settingsFromHook }], settingsFromHook.debugKey);
                                }
                                else {
                                    fetch(settingsFromHook, paramHashFromHook, 'auto');
                                }
                            }
                        }
                    }, [paramHashFromHook]);
                    /** REFETCH QUERY TRIGGER EFFECTS */
                    // called when the entire cache is reset by a refetch query passed into a mutation
                    React.useEffect(() => {
                        var _a, _b;
                        if (!isFirstRender.current) {
                            if (storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.shouldRefetchData) {
                                let paramsToUse = (_a = lastUsedSettings.current) === null || _a === void 0 ? void 0 : _a.parameters;
                                let forceExclusiveParams = false;
                                if (storedStateSlice.shouldRefetchData.params) {
                                    switch ((_b = storedStateSlice.shouldRefetchData.paramMode) !== null && _b !== void 0 ? _b : 'merge') {
                                        case 'merge':
                                            paramsToUse = Objects.mergeDeep(paramsToUse, storedStateSlice.shouldRefetchData.params);
                                            break;
                                        case 'replace':
                                            paramsToUse = storedStateSlice.shouldRefetchData.params;
                                            forceExclusiveParams = true;
                                            break;
                                        default:
                                            throw new Error(`Invalid param override mode sent to refetch query ${storedStateSlice.shouldRefetchData.paramMode}`);
                                    }
                                }
                                queryLog(['Refetch triggered', { parameters: paramsToUse }], settingsFromHook.debugKey);
                                invoke(paramsToUse, undefined, 'refetch', forceExclusiveParams);
                            }
                        }
                    }, [!!(storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.shouldRefetchData)]);
                    /** UTILS */
                    // mark the end of the first render - must come last
                    React.useEffect(() => {
                        isFirstRender.current = false;
                    }, [cacheKey]);
                    /** PROCESSING HOOK */
                    const processingHookDetails = React.useMemo(() => {
                        return {
                            endpointID: endpointHash,
                            fetchingMode: valueToReturn.fetchingMode,
                            hookType: 'query',
                            data: valueToReturn.data,
                            error: valueToReturn.error,
                            settings: lastUsedSettings.current,
                            fetch: manualInvoke,
                        };
                    }, [valueToReturn, lastUsedSettings.current, manualInvoke]);
                    const processed = processingHook === null || processingHook === void 0 ? void 0 : processingHook(processingHookDetails);
                    React.useEffect(() => {
                        if (processingHook) {
                            queryLog([`Processing hook executed`, { hookType: 'query', data: storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.data, processed }], settingsFromHook.debugKey);
                        }
                    }, [storedStateSlice === null || storedStateSlice === void 0 ? void 0 : storedStateSlice.data]);
                    /** RETURN FROM HOOK */
                    const valueToReturnWithProcessed = React.useMemo(() => (Object.assign(Object.assign({}, valueToReturn), { processed })), [valueToReturn, processed]);
                    // return the state value and the fetch method from the hook
                    return [valueToReturnWithProcessed, manualInvoke, manualSet];
                },
                /**
                 * useMutation
                 * - The hook to be used if the endpoint is a POST/PUT/DELETE and should NOT interface with the caching system
                 * - Receives the hook execution level settings to override the system, application and hook level.
                 * - Returns the fetch method to manually invoke, and a live response object
                 * - Can only be used within a React Function Component
                 */
                useMutation: (executionSettings = {}) => {
                    /** MARK ENDPOINT AS USED */
                    React.useEffect(() => {
                        endpointUsed('mutation');
                    }, []);
                    // store response in state to return as index 1 from the hook
                    const [fetchStateResponse, setFetchStateResponse] = React.useReducer(mutationFetchResponseReducer, {
                        fetchingMode: 'not-fetching',
                        isFetching: false,
                        data: undefined,
                        error: undefined,
                        endpointID: endpointHash,
                    });
                    // get the dispatcher and test keys from context
                    const [, dispatch, testKeys] = React.useContext(ApiHooksStore.Context);
                    // store the last used fetch settings in a ref so that they can be passed to the processing hook.
                    const lastUsedSettings = React.useRef();
                    // retrieve the pre-processor if available
                    const preProcessor = preProcessorHook === null || preProcessorHook === void 0 ? void 0 : preProcessorHook();
                    // settings - apply the hook execution settings (if any) to the passed in system, application and endpoint level.
                    // NOTE - the JSON.stringify prevents the need for the consumer to memoize the incoming execution settings, it's not ideal, but it's only a small object so it should be ok.
                    const settingsFromHook = React.useMemo(() => {
                        return Objects.mergeDeep(combinedMutationSettings, executionSettings);
                    }, [
                        JSON.stringify(executionSettings),
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchComplete,
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchError,
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchStart,
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchSuccess,
                    ]);
                    // the method used to dispatch refetch actions - these trigger the "refetch query" behaviour.
                    const refetchQueries = React.useCallback((queries) => {
                        var _a, _b, _c;
                        const settingsToUse = (_a = lastUsedSettings.current) !== null && _a !== void 0 ? _a : settingsFromHook;
                        const parsedQueries = typeof queries === 'function' ? queries(settingsToUse) : queries;
                        for (const query of parsedQueries) {
                            let finalCacheKeyValue;
                            let queryConfig;
                            try {
                                finalCacheKeyValue = ApiHooksCaching.cacheKeyValueFromRefetchQuery(settingsToUse.parameters, query, settingsToUse.refetchQueryContext);
                                if (query.paramOverride) {
                                    queryConfig = {
                                        params: Objects.deepClone(query.paramOverride),
                                        paramMode: (_b = query.paramOverrideMode) !== null && _b !== void 0 ? _b : 'merge',
                                    };
                                }
                            }
                            catch (error) {
                                throw new Error(`API Hooks Mutation Error, Endpoint: ${endpointHash} - ${(_c = error === null || error === void 0 ? void 0 : error.message) !== null && _c !== void 0 ? _c : 'Refetch query failed'}`);
                            }
                            mutationLog([`Refetch query processed`, { query, finalCacheKeyValue }], settingsToUse.debugKey);
                            dispatch === null || dispatch === void 0 ? void 0 : dispatch(ApiHooksStore.Actions.refetch(query.endpointHash, finalCacheKeyValue === null || finalCacheKeyValue === void 0 ? void 0 : finalCacheKeyValue.toString(), queryConfig));
                        }
                    }, [dispatch, settingsFromHook]);
                    // fetch method - detached from cache, calls API and returns a promise
                    const fetch = React.useCallback(async (param, settings) => {
                        var _a, _b, _c, _d, _e;
                        // combine all the settings together in order to include system, application, endpoint, execution and fetch level, as well as the passed in params.
                        const finalSettings = Objects.mergeDeep(settingsFromHook, settings || {}, { parameters: param || {} });
                        // set live response to loading
                        mutationLog([`Fetch started`, { finalSettings }], finalSettings.debugKey);
                        setFetchStateResponse({ fetchingMode: 'manual', isFetching: true });
                        ApiHooksEvents.onFetchStart.executeEventHooks(endpointHash, finalSettings.parameters, 'mutation');
                        (_a = finalSettings.onFetchStart) === null || _a === void 0 ? void 0 : _a.call(finalSettings, finalSettings, 'manual');
                        // call the pre-processor if it exists
                        const shouldContinue = preProcessor
                            ? await preProcessor({ hookType: 'mutation', endpointID: endpointHash, settings: finalSettings })
                            : true;
                        // abort if pre-processor returns false
                        if (!shouldContinue) {
                            setFetchStateResponse({ data: undefined, fetchingMode: 'aborted', isFetching: false });
                            mutationLog([`Mutation aborted by pre-processor`, { finalSettings }], finalSettings.debugKey);
                            return undefined;
                        }
                        // store final settings used for processing hook and refetch queries.
                        lastUsedSettings.current = finalSettings;
                        // fetch the data value from either the real or mock endpoint, depending on the settings
                        let value;
                        let error;
                        try {
                            if (testKeys || finalSettings.useMockEndpoints) {
                                if (!mockPromiseFactory) {
                                    throw new Error(`API Hooks error - no mock endpoint has been defined for the following mutation: ${endpointHash}`);
                                }
                                value = await mockPromiseFactory(finalSettings.parameters, (_b = testKeys === null || testKeys === void 0 ? void 0 : testKeys[endpointKey]) === null || _b === void 0 ? void 0 : _b.testKey);
                            }
                            else {
                                value = await promiseFactory(finalSettings.parameters);
                            }
                            // set live response to success
                            setFetchStateResponse({ data: value, fetchingMode: 'not-fetching', isFetching: false, error: undefined });
                            mutationLog([`Fetch successful`, { finalSettings, response: value }], finalSettings.debugKey);
                            ApiHooksEvents.onFetchSuccess.executeEventHooks(endpointHash, finalSettings.parameters, 'mutation', value);
                            (_c = finalSettings.onFetchSuccess) === null || _c === void 0 ? void 0 : _c.call(finalSettings, value, finalSettings);
                            // handle any refetch queries that were passed in.
                            if (finalSettings.refetchQueries) {
                                refetchQueries(finalSettings.refetchQueries);
                            }
                        }
                        catch (e) {
                            // set live response to failed
                            error = e;
                            setFetchStateResponse({ data: undefined, fetchingMode: 'not-fetching', isFetching: false, error });
                            mutationLog([`Fetch failed`, { error }], finalSettings.debugKey);
                            ApiHooksEvents.onFetchError.executeEventHooks(endpointHash, finalSettings.parameters, 'mutation', error);
                            (_d = finalSettings.onFetchError) === null || _d === void 0 ? void 0 : _d.call(finalSettings, error, finalSettings);
                        }
                        finally {
                            (_e = finalSettings.onFetchComplete) === null || _e === void 0 ? void 0 : _e.call(finalSettings, value, error, finalSettings);
                            // run responder callbacks
                            ApiHooksResponders.registeredMutationListeners
                                .filter((rl) => rl.endpointHash === endpointHash)
                                .forEach((rl) => {
                                mutationLog(['Executing mutation responder listener'], finalSettings.debugKey);
                                rl.callback({ data: value, error, params: finalSettings.parameters, settings: finalSettings });
                            });
                        }
                        // return the data, errors will be thrown for mutations and should be handled by the consuming component unless `throwErrors` is explicitly set to false in settings.
                        if (error && finalSettings.throwErrors) {
                            throw error;
                        }
                        return value;
                    }, [settingsFromHook, refetchQueries, preProcessor]);
                    const processingHookDetails = React.useMemo(() => {
                        return {
                            endpointID: endpointHash,
                            fetchingMode: fetchStateResponse.fetchingMode,
                            hookType: 'mutation',
                            data: fetchStateResponse.data,
                            error: fetchStateResponse.error,
                            settings: lastUsedSettings.current,
                            fetch,
                        };
                    }, [fetchStateResponse, lastUsedSettings.current, fetch]);
                    // run the processing hook
                    const processed = processingHook === null || processingHook === void 0 ? void 0 : processingHook(processingHookDetails);
                    React.useEffect(() => {
                        if (processingHook) {
                            mutationLog([`Processing hook executed`, { hookType: 'mutation', data: fetchStateResponse.data, processed }], settingsFromHook.debugKey);
                        }
                    }, [fetchStateResponse.data]);
                    // compile the live response
                    const liveResponse = React.useMemo(() => (Object.assign(Object.assign({}, fetchStateResponse), { processed })), [fetchStateResponse, processed]);
                    return [fetch, liveResponse, refetchQueries];
                },
                /**
                 * useRequest
                 * - The hook to be used for any request for which we ONLY want the promise, no local state or caching at all.
                 * - Receives the hook execution level settings to override the system, application and hook level.
                 * - Returns the detached fetch method to manually invoke
                 * - Can only be used within a React Function Component
                 */
                useRequest: (executionSettings = {}) => {
                    // settings - apply the hook execution settings (if any) to the passed in system, application and endpoint level.
                    // NOTE - the JSON.stringify prevents the need for the consumer to memoize the incoming execution settings, it's not ideal, but it's only a small object so it should be ok.
                    const settingsFromHook = React.useMemo(() => {
                        var _a;
                        const settings = Object.assign(Object.assign({}, combinedRequestSettings), executionSettings);
                        settings.parameters = Object.assign(Object.assign({}, combinedMutationSettings.parameters), ((_a = executionSettings.parameters) !== null && _a !== void 0 ? _a : {}));
                        return settings;
                    }, [
                        JSON.stringify(executionSettings),
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchComplete,
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchError,
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchStart,
                        executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.onFetchSuccess,
                    ]);
                    // get any test keys
                    const [, , testKeys] = React.useContext(ApiHooksStore.Context);
                    // fetch method - detached from cache, calls API and returns a promise
                    const fetch = React.useCallback(async (param, settings) => {
                        var _a, _b, _c, _d, _e, _f;
                        // combine all the settings together in order to include system, application, endpoint, execution and fetch level, as well as the passed in params.
                        const finalSettings = Object.assign(Object.assign(Object.assign({}, settingsFromHook), (settings || {})), (param ? { parameters: Object.assign(Object.assign({}, ((_a = settingsFromHook.parameters) !== null && _a !== void 0 ? _a : {})), param) } : {}));
                        requestLog([`Fetch started`, { finalSettings }], finalSettings.debugKey);
                        ApiHooksEvents.onFetchStart.executeEventHooks(endpointHash, finalSettings.parameters, 'request');
                        (_b = finalSettings.onFetchStart) === null || _b === void 0 ? void 0 : _b.call(finalSettings, finalSettings, 'manual');
                        // fetch the data value from either the real or mock endpoint, depending on the settings
                        let value;
                        let error;
                        try {
                            if (testKeys || (finalSettings === null || finalSettings === void 0 ? void 0 : finalSettings.useMockEndpoints)) {
                                if (!mockPromiseFactory) {
                                    throw new Error(`API Hooks error - no mock endpoint has been defined for the following request: ${endpointHash}`);
                                }
                                value = await mockPromiseFactory(finalSettings.parameters, (_c = testKeys === null || testKeys === void 0 ? void 0 : testKeys[endpointKey]) === null || _c === void 0 ? void 0 : _c.testKey);
                            }
                            else {
                                value = await promiseFactory(finalSettings.parameters);
                            }
                            requestLog([`Fetch successful`, { finalSettings, response: value }], finalSettings.debugKey);
                            ApiHooksEvents.onFetchSuccess.executeEventHooks(endpointHash, finalSettings.parameters, 'request', value);
                            (_d = finalSettings.onFetchSuccess) === null || _d === void 0 ? void 0 : _d.call(finalSettings, value, finalSettings);
                        }
                        catch (e) {
                            // set live response to failed
                            error = e;
                            requestLog([`Fetch failed`, { finalSettings, error }], finalSettings.debugKey);
                            ApiHooksEvents.onFetchError.executeEventHooks(endpointHash, finalSettings.parameters, 'request', error);
                            (_e = finalSettings.onFetchError) === null || _e === void 0 ? void 0 : _e.call(finalSettings, error, finalSettings);
                        }
                        finally {
                            (_f = finalSettings.onFetchComplete) === null || _f === void 0 ? void 0 : _f.call(finalSettings, value, error, finalSettings);
                            // run responder callbacks
                            ApiHooksResponders.registeredRequestListeners
                                .filter((rl) => rl.endpointHash === endpointHash)
                                .forEach((rl) => {
                                requestLog(['Executing request responder listener'], finalSettings.debugKey);
                                rl.callback({ data: value, error, params: finalSettings.parameters, settings: finalSettings });
                            });
                        }
                        // return the data, errors will be thrown for requests and should be handled by the consuming components.
                        if (error) {
                            throw error;
                        }
                        return value;
                    }, [settingsFromHook, testKeys]);
                    return fetch;
                },
                /**
                 * useTools
                 * - This hook returns a set of useful tools relating to the endpoint in question.
                 * - Receives execution level settings only.
                 */
                useTools: (executionSettings = {}) => {
                    const [state, dispatch] = React.useContext(ApiHooksStore.Context);
                    const myState = state[endpointHash];
                    const debugKey = executionSettings === null || executionSettings === void 0 ? void 0 : executionSettings.debugKey;
                    const refetchAllQueries = React.useCallback((config) => {
                        if (!myState) {
                            toolsLog(['Refetch triggered but no state for endpoint'], debugKey);
                            return;
                        }
                        const cacheKeysToFetch = Array.isArray(config) && config.length ? config.map((c) => c.cacheKeyValue) : Object.keys(myState);
                        for (const cacheKeyValue of cacheKeysToFetch) {
                            const myStateSlice = myState[cacheKeyValue];
                            if (!myStateSlice) {
                                toolsLog(['Refetch triggered but no state for endpoint with cacheKey value', { cacheKeyValue }], debugKey);
                                continue;
                            }
                            const myConfig = Array.isArray(config) ? config.find((c) => c.cacheKeyValue === cacheKeyValue) : config;
                            toolsLog(['Processing refetch with config', { cacheKeyValue, config: myConfig }], debugKey);
                            dispatch === null || dispatch === void 0 ? void 0 : dispatch(ApiHooksStore.Actions.refetch(endpointHash, cacheKeyValue.toString(), myConfig && Object.assign({}, myConfig)));
                        }
                    }, [myState, debugKey]);
                    return { refetchAllQueries };
                },
            };
            return controllerDictionary;
        }, {});
    }
    /**
     * The function that merges the system settings with any root settings passed to application level config.
     * Used by the "create" and "createMulti" methods
     * @param config The application level config for the useQuery and useMutation hooks. Overrides the system config but can potentially be overridden at endpoint and hook level
     * @returns Three dictionaries of merged settings for the three hook types.
     */
    function mergeRootSettings(config) {
        var _a, _b, _c, _d, _e;
        // apply application level query settings onto system level if any
        const rootQuerySettings = Object.assign(Object.assign({}, ApiHooksSystemSettings.systemDefaultQuery), ((_a = config.queryConfig) !== null && _a !== void 0 ? _a : {}));
        // apply application level query caching settings onto system level if any
        rootQuerySettings.caching = Object.assign(Object.assign({}, rootQuerySettings.caching), ((_c = (_b = config.queryConfig) === null || _b === void 0 ? void 0 : _b.caching) !== null && _c !== void 0 ? _c : {}));
        // apply application level mutation settings onto system level if any
        const rootMutationSettings = Object.assign(Object.assign({}, ApiHooksSystemSettings.systemDefaultMutation), ((_d = config.mutationConfig) !== null && _d !== void 0 ? _d : {}));
        // apply application level request settings onto system level if any
        const rootRequestSettings = Object.assign(Object.assign({}, ApiHooksSystemSettings.systemDefaultRequest), ((_e = config.requestConfig) !== null && _e !== void 0 ? _e : {}));
        return { rootQuerySettings, rootMutationSettings, rootRequestSettings };
    }
    /**
     * The create function takes a single API client and returns the hooks, config dictionaries, and mock endpoints dictionary.
     * @param apiClient The API client to parse, must be an object containing controller objects with nested endpoint functions
     * @param config The application level config for the useQuery and useMutation hooks. Overrides the system config but can potentially be overridden at endpoint and hook level
     * @returns The hooks
     */
    function create(apiClient, config = {}) {
        var _a, _b, _c, _d, _e, _f, _g;
        if ((_a = config === null || config === void 0 ? void 0 : config.generalConfig) === null || _a === void 0 ? void 0 : _a.debugMode) {
            log('API Hooks - creating hook/config library for (client/applicationConfig):', apiClient, config);
        }
        // merge root level settings
        const { rootQuerySettings, rootMutationSettings, rootRequestSettings } = mergeRootSettings(config);
        // create empty library or controller/endpoint objects to pass to settings factory and mock endpoint factory.
        const emptyHookConfigLibrary = createEmptyHookLibraryDefaults(apiClient);
        const emptyMockEndpointLibrary = createEmptyHookLibraryDefaults(apiClient);
        const emptyDefaultDataLibrary = createEmptyHookLibraryDefaults(apiClient);
        // create hook config object from factory function
        const hookConfig = (_c = (_b = config === null || config === void 0 ? void 0 : config.hookConfigFactory) === null || _b === void 0 ? void 0 : _b.call(config, emptyHookConfigLibrary)) !== null && _c !== void 0 ? _c : emptyHookConfigLibrary;
        const mockEndpoints = (_e = (_d = config === null || config === void 0 ? void 0 : config.mockEndpointFactory) === null || _d === void 0 ? void 0 : _d.call(config, emptyMockEndpointLibrary)) !== null && _e !== void 0 ? _e : emptyMockEndpointLibrary;
        const defaultData = (_g = (_f = config === null || config === void 0 ? void 0 : config.defaultDataFactory) === null || _f === void 0 ? void 0 : _f.call(config, emptyDefaultDataLibrary)) !== null && _g !== void 0 ? _g : emptyDefaultDataLibrary;
        // Reduce client controller dictionary into hooks and config
        return Object.keys(apiClient).reduce((memo, key) => {
            const newMemo = Object.assign({}, memo);
            const controller = apiClient[key];
            newMemo[key] = createHooks(key, controller, rootQuerySettings, rootMutationSettings, rootRequestSettings, hookConfig[key], mockEndpoints[key], defaultData[key], config === null || config === void 0 ? void 0 : config.generalConfig, config === null || config === void 0 ? void 0 : config.processingHook, config === null || config === void 0 ? void 0 : config.preProcessorHook);
            return newMemo;
        }, {});
    }
    ApiHooks.create = create;
    /**
     * The create function takes a dictionary of API clients and returns the hooks, config dictionaries, and mock endpoints dictionary.
     * @param apiClientDictionary A dictionary of API clients to parse, must be an object containing client key strings mapped to clients containing controller objects with nested endpoint functions
     * @param config The application level config for the useQuery and useMutation hooks. Overrides the system config but can potentially be overridden at endpoint and hook level
     * @returns The hooks
     */
    function createMulti(apiClientDictionary, config = {}) {
        var _a, _b, _c, _d, _e, _f, _g;
        if ((_a = config === null || config === void 0 ? void 0 : config.generalConfig) === null || _a === void 0 ? void 0 : _a.debugMode) {
            log('API Hooks - creating "multi" hook/config library for (client/applicationConfig):', apiClientDictionary, config);
        }
        // merge root level settings
        const { rootQuerySettings, rootMutationSettings, rootRequestSettings } = mergeRootSettings(config);
        const createEmptyLibrary = () => {
            return Object.keys(apiClientDictionary).reduce((finalLibrary, incomingClientKey) => (Object.assign(Object.assign({}, finalLibrary), { [incomingClientKey]: createEmptyHookLibraryDefaults(apiClientDictionary[incomingClientKey]) })), {});
        };
        // create empty library or controller/endpoint objects to pass to settings factory and mock endpoint factory.
        const emptyHookConfigLibrary = createEmptyLibrary();
        const emptyMockEndpointLibrary = createEmptyLibrary();
        const emptyDefaultDataLibrary = createEmptyLibrary();
        // create hook config object from factory function
        const clientHookConfig = (_c = (_b = config === null || config === void 0 ? void 0 : config.hookConfigFactory) === null || _b === void 0 ? void 0 : _b.call(config, emptyHookConfigLibrary)) !== null && _c !== void 0 ? _c : emptyHookConfigLibrary;
        const clientMockEndpoints = (_e = (_d = config === null || config === void 0 ? void 0 : config.mockEndpointFactory) === null || _d === void 0 ? void 0 : _d.call(config, emptyMockEndpointLibrary)) !== null && _e !== void 0 ? _e : emptyMockEndpointLibrary;
        const clientDefaultData = (_g = (_f = config === null || config === void 0 ? void 0 : config.defaultDataFactory) === null || _f === void 0 ? void 0 : _f.call(config, emptyDefaultDataLibrary)) !== null && _g !== void 0 ? _g : emptyDefaultDataLibrary;
        return Object.keys(apiClientDictionary).reduce((clientMemo, clientKey) => {
            const newClientMemo = Object.assign({}, clientMemo);
            const apiClient = apiClientDictionary[clientKey];
            const hookConfig = clientHookConfig[clientKey];
            const mockEndpoints = clientMockEndpoints[clientKey];
            const defaultData = clientDefaultData[clientKey];
            newClientMemo[clientKey] = Object.keys(apiClient).reduce((memo, key) => {
                const newMemo = Object.assign({}, memo);
                const controller = apiClient[key];
                newMemo[key] = createHooks(key, controller, rootQuerySettings, rootMutationSettings, rootRequestSettings, hookConfig[key], mockEndpoints[key], defaultData[key], config === null || config === void 0 ? void 0 : config.generalConfig);
                return newMemo;
            }, {});
            return newClientMemo;
        }, {});
    }
    ApiHooks.createMulti = createMulti;
})(ApiHooks || (ApiHooks = {}));
